import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChatBubbleOvalLeftIcon,
  CalendarIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  MapPinIcon,
  ClockIcon,
  UserGroupIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';

const Community = () => {
  const [activeTab, setActiveTab] = useState('forums');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newThread, setNewThread] = useState({
    title: '',
    content: '',
    category: '',
    tags: '',
  });
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    category: '',
    locationType: 'online',
    location: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    maxAttendees: '',
    image_url: '',
    tags: '',
  });
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(null);

  const forums = [
    {
      id: 1,
      title: 'Getting Started with React',
      category: 'Development',
      author: 'John Doe',
      replies: 24,
      views: 156,
      lastActivity: '2 hours ago',
    },
    {
      id: 2,
      title: 'Best Practices for UI/UX Design',
      category: 'Design',
      author: 'Jane Smith',
      replies: 18,
      views: 98,
      lastActivity: '5 hours ago',
    },
    {
      id: 3,
      title: 'Building Scalable Backend APIs',
      category: 'Development',
      author: 'Mike Johnson',
      replies: 32,
      views: 245,
      lastActivity: '1 day ago',
    },
    {
      id: 4,
      title: 'Startup Funding Strategies',
      category: 'Business',
      author: 'Sarah Wilson',
      replies: 15,
      views: 89,
      lastActivity: '2 days ago',
    },
  ];

  const categories = ['Development', 'Design', 'Business', 'AI/ML', 'Startup', 'General'];
  const eventCategories = ['Workshop', 'conference', 'Networking', 'Meetup', 'Webinar', 'Hackathon'];

  // Fetch all threads
  useEffect(() => {
    const fetchThreads = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.forums.getThreads();
        setThreads(res.threads || res || []);
      } catch (err) {
        setError('Failed to load forum threads.');
      } finally {
        setLoading(false);
      }
    };
    fetchThreads();
  }, []);

  // Fetch events
  useEffect(() => {
    if (activeTab === 'events') {
      setEventsLoading(true);
      setEventsError(null);
      api.events.getAll()
        .then(data => setEvents(data.events || []))
        .catch(() => setEventsError('Failed to load events.'))
        .finally(() => setEventsLoading(false));
    }
  }, [activeTab]);

  // Create new thread
  const handleCreateThread = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.forums.createThread({
        title: newThread.title,
        content: newThread.content,
        category: newThread.category,
        tags: newThread.tags ? newThread.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      });
      setShowCreateModal(false);
      setNewThread({ title: '', content: '', category: '', tags: '' });
      // Refresh threads
      const res = await api.forums.getThreads();
      setThreads(res.threads || res || []);
    } catch (err) {
      alert('Failed to create thread.');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      // Combine date and time for start and end
      const startDateTime = newEvent.startDate && newEvent.startTime
        ? new Date(`${newEvent.startDate}T${newEvent.startTime}`).toISOString()
        : undefined;
      const endDateTime = newEvent.endDate && newEvent.endTime
        ? new Date(`${newEvent.endDate}T${newEvent.endTime}`).toISOString()
        : undefined;
      const eventData = {
        title: newEvent.title,
        description: newEvent.description,
        category: newEvent.category,
        location_type: newEvent.locationType,
        location: newEvent.location,
        start_date: startDateTime,
        end_date: endDateTime,
        max_participants: newEvent.maxAttendees ? parseInt(newEvent.maxAttendees) : 0,
        image_url: newEvent.image_url || '',
        tags: newEvent.tags ? newEvent.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      await api.events.create(eventData);
      setShowCreateModal(false);
      setNewEvent({
        title: '',
        description: '',
        category: '',
        locationType: 'online',
        location: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        maxAttendees: '',
        image_url: '',
        tags: '',
      });
      // Refresh events
      setEventsLoading(true);
      const data = await api.events.getAll();
      setEvents(data.events || []);
      setEventsLoading(false);
    } catch (err) {
      alert('Failed to create event.');
    } finally {
      setCreating(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (activeTab === 'forums') {
      setNewThread(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setNewEvent(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleRegisterForEvent = (eventId) => {
    // TODO: Implement event registration logic
    console.log('Registering for event:', eventId);
    alert('Successfully registered for the event! You will receive a confirmation email shortly.');
  };

  // Filtered threads
  const filteredThreads = threads.filter((thread) =>
    thread.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    thread.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    thread.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Community
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Connect with other members, join discussions, and participate in events.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          {activeTab === 'forums' ? 'Create Thread' : 'Create Event'}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('forums')}
            className={`${
              activeTab === 'forums'
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <ChatBubbleOvalLeftIcon className="h-5 w-5 mr-2" />
            Forums
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`${
              activeTab === 'events'
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <CalendarIcon className="h-5 w-5 mr-2" />
            Events
          </button>
        </nav>
      </div>

      {/* Search Bar */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-gray-400" />
            <select className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option>All Categories</option>
              {activeTab === 'forums' ? (
                categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))
              ) : (
                eventCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'forums' && (
        <div className="space-y-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-600 dark:text-red-400">{error}</div>
          ) : filteredThreads.length === 0 ? (
            <div className="text-center py-12 text-gray-600 dark:text-gray-300">No threads found.</div>
          ) : (
            filteredThreads.map((thread) => (
              <div
                key={thread.id}
                className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-2">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
                      {thread.title}
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(Array.isArray(thread.tags) ? thread.tags : []).map((tag) => (
                        <span
                          key={tag}
                          className="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-2 py-1 rounded-full text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mb-1">
                      <span>Category: {thread.category}</span>
                      <span>By {thread.author_name}</span>
                      <span><ClockIcon className="h-4 w-4 inline mr-1" />{new Date(thread.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <Link
                    to={`/forums/${thread.id}`}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    View Thread
                  </Link>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-2 line-clamp-3">
                  {thread.content}
                </p>
                <div className="flex items-center gap-6 text-xs text-gray-500 dark:text-gray-400">
                  <span>Replies: {thread.replies_count}</span>
                  <span>Views: {thread.views_count}</span>
                  <span>Updated: {new Date(thread.updated_at).toLocaleString()}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'events' && (  
        
        eventsLoading ? (
          
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : eventsError ? (
          <div className="text-center text-red-600 dark:text-red-400 py-12">{eventsError}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {events.length === 0 ? (
              <div className="col-span-full text-gray-500 dark:text-gray-400">No events found.</div>
            ) : (
              events.map((event) => (
                <div
                  key={event.id}
                  className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                          {event.title}
                        </h3>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                          {event.category}
                        </span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                        {event.description}
                      </p>
                    </div>
                  </div>
                  {/* Event image or SVG placeholder */}
                  {event.image_url ? (
                    <img src={event.image_url} alt={event.title} className="w-full h-40 object-cover rounded-lg mb-4" />
                  ) : (
                    (() => {
                      // Deterministic gradient selection based on event id or title
                      const gradients = [
                        ['from-pink-200', 'to-yellow-200', 'dark:from-pink-900', 'dark:to-yellow-900'],
                        ['from-indigo-100', 'to-purple-100', 'dark:from-indigo-900', 'dark:to-purple-900'],
                        ['from-green-100', 'to-blue-100', 'dark:from-green-900', 'dark:to-blue-900'],
                        ['from-yellow-100', 'to-pink-100', 'dark:from-yellow-900', 'dark:to-pink-900'],
                        ['from-blue-100', 'to-cyan-100', 'dark:from-blue-900', 'dark:to-cyan-900'],
                        ['from-purple-100', 'to-fuchsia-100', 'dark:from-purple-900', 'dark:to-fuchsia-900'],
                      ];
                      const hash = (event.id || event.title || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
                      const grad = gradients[hash % gradients.length];
                      return (
                        <div className={`w-full h-40 flex items-center justify-center rounded-lg mb-4 bg-gradient-to-br ${grad[0]} ${grad[1]} ${grad[2]} ${grad[3]}`}> 
                          <svg
                            width="80"
                            height="80"
                            viewBox="0 0 48 48"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="opacity-90"
                            aria-label="Event placeholder"
                          >
                            {/* Background circle */}
                            <circle cx="24" cy="24" r="22" fill="#c7d2fe" opacity="0.3" />
                            {/* Main icon: calendar */}
                            <rect x="12" y="16" width="24" height="18" rx="4" fill="#6366f1" />
                            <rect x="12" y="16" width="24" height="4" rx="2" fill="#818cf8" />
                            <rect x="18" y="24" width="12" height="2.5" rx="1.25" fill="#fff" opacity="0.7" />
                            <rect x="18" y="28" width="8" height="2" rx="1" fill="#fff" opacity="0.5" />
                            {/* Decorative dots */}
                            <circle cx="8" cy="10" r="1" fill="#fbbf24" />
                            <circle cx="40" cy="12" r="1.2" fill="#f472b6" />
                            <circle cx="24" cy="44" r="0.8" fill="#34d399" />
                          </svg>
                        </div>
                      );
                    })()
                  )}
                  {/* Event Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      <span>{new Date(event.start_date).toLocaleString()} - {new Date(event.end_date).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                      {event.location_type === 'online' ? (
                        <GlobeAltIcon className="h-4 w-4 mr-2" />
                      ) : (
                        <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                      )}
                      <span>{event.location}</span>
                      {event.location_type === 'online' && (
                        <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full text-xs">
                          Remote
                        </span>
                      )}
                      {event.location_type === 'onsite' && (
                        <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-xs">
                          On-site
                        </span>
                      )}
                    </div>
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                      <UserGroupIcon className="h-4 w-4 mr-2" />
                      <span>{event.participants_count}/{event.max_participants} participants</span>
                    </div>
                  </div>
                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-1">
                      <span>Registration</span>
                      <span>{event.max_participants > 0 ? Math.round((event.participants_count / event.max_participants) * 100) : 0}% full</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full"
                        style={{ width: `${event.max_participants > 0 ? (event.participants_count / event.max_participants) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Action Button */}
                  <div className="flex justify-between items-center">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      By {event.organizer_name}
                    </div>
                    <div className="flex space-x-2">
                      <Link
                        to={`/events/${event.id}`}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )
      )}

      {/* Create Thread/Event Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {activeTab === 'forums' ? 'Create New Thread' : 'Create New Event'}
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {activeTab === 'forums' ? (
              <form onSubmit={handleCreateThread} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={newThread.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Content
                  </label>
                  <textarea
                    name="content"
                    value={newThread.content}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category
                  </label>
                  <input
                    type="text"
                    name="category"
                    value={newThread.category}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tags (comma separated)
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={newThread.tags}
                    onChange={handleInputChange}
                    placeholder="e.g. react, help, question"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    disabled={creating}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {creating ? 'Creating...' : 'Create Thread'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleCreateEvent} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Event Title</label>
                    <input type="text" name="title" value={newEvent.title} onChange={handleInputChange} required className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" placeholder="Enter event title..." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                    <select name="category" value={newEvent.category} onChange={handleInputChange} required className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                      <option value="">Select Category</option>
                      {eventCategories.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <textarea name="description" value={newEvent.description} onChange={handleInputChange} required rows={3} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" placeholder="Describe your event..." />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                    <input type="date" name="startDate" value={newEvent.startDate} onChange={handleInputChange} required className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Time</label>
                    <input type="time" name="startTime" value={newEvent.startTime} onChange={handleInputChange} required className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                    <input type="date" name="endDate" value={newEvent.endDate} onChange={handleInputChange} required className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Time</label>
                    <input type="time" name="endTime" value={newEvent.endTime} onChange={handleInputChange} required className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location Type</label>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input type="radio" name="locationType" value="online" checked={newEvent.locationType === 'online'} onChange={handleInputChange} className="mr-2" />
                        <span className="text-gray-700 dark:text-gray-300">Online</span>
                      </label>
                      <label className="flex items-center">
                        <input type="radio" name="locationType" value="onsite" checked={newEvent.locationType === 'onsite'} onChange={handleInputChange} className="mr-2" />
                        <span className="text-gray-700 dark:text-gray-300">On-site</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location</label>
                    <input type="text" name="location" value={newEvent.location} onChange={handleInputChange} required placeholder={newEvent.locationType === 'online' ? 'Zoom, Google Meet, etc.' : 'City or venue name'} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Maximum Participants</label>
                    <input type="number" name="maxAttendees" value={newEvent.maxAttendees} onChange={handleInputChange} min="1" placeholder="e.g., 100" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Image URL</label>
                    <input type="text" name="image_url" value={newEvent.image_url} onChange={handleInputChange} placeholder="https://..." className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags (comma separated)</label>
                  <input type="text" name="tags" value={newEvent.tags} onChange={handleInputChange} placeholder="e.g. ai, conference, networking" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500">{creating ? 'Creating...' : 'Create Event'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Community;